import React, { useState } from 'react'

import { DropdownMenu } from '@app/components'
import { LntDropdown } from '@app/components/base'

import { useToggle } from '@app/hooks'
import { findSelectedDropdownData } from '@app/components/DropdownMenu/utils'

import { Trigger } from './Trigger'

import type { ReactNode } from 'react'
import type { DropdownMenuItem, DropdownData } from '@app/types/dropdown'
import type { ExtendedDropdownProps, TriggerProps } from './types'

const DefaultTrigger: React.FC<TriggerProps> = ({ label, isOpen, toggle, triggerProps }) => (
  <Trigger
    isOpen={ isOpen }
    onClick={ toggle }
    { ...triggerProps }
  >
    { label }
  </Trigger>
)

export const ExtendedDropdown: React.FC<ExtendedDropdownProps> = ({
  trigger: Trigger = DefaultTrigger,
  items = [],
  defaultItems = items,
  label,
  toolbar,
  className,
  isSortable,
  isMultiple,
  isSearchable,
  dropdownTriggerComponentProps,
  onAccept,
  onSelect,
  onSort
}) => {
  const [dropdownMenuItems, setDropdownMenuItems] = useState<DropdownMenuItem[]>(items)

  const dropdownOpenToggle = useToggle()

  const handleSelect = (items: DropdownMenuItem[], item?: DropdownMenuItem): void => {
    const selectedData: DropdownData[] = findSelectedDropdownData(items)

    onSelect?.(selectedData, items, item)

    setDropdownMenuItems(items)
  }

  const handleSort = (items: DropdownMenuItem[]): void => {
    const selectedData: DropdownData[] = findSelectedDropdownData(items)

    onSort?.(selectedData, items)

    setDropdownMenuItems(items)
  }

  const handleAccept = (items: DropdownMenuItem[]): void => {
    const selectedData: DropdownData[] = findSelectedDropdownData(items)

    onAccept?.(selectedData, items)

    dropdownOpenToggle.setUntoggled()
  }

  const dropdownRender = (): ReactNode => (
    <DropdownMenu
      items={ dropdownMenuItems }
      defaultItems={ defaultItems }
      isSortable={ isSortable }
      isMultiple={ isMultiple }
      isSearchable={ isSearchable }
      toolbar={ toolbar }
      onAccept={ handleAccept }
      onSelect={ handleSelect }
      onChange={ handleSort }
    />
  )

  return (
    <LntDropdown
      className={ className }
      trigger={ ['click'] }
      dropdownRender={ dropdownRender }
      open={ dropdownOpenToggle.value }
      onOpenChange={ dropdownOpenToggle.toggle }
    >
      <Trigger
        label={ label }
        isOpen={ dropdownOpenToggle.value }
        toggle={ dropdownOpenToggle.toggle }
        triggerProps={ dropdownTriggerComponentProps }
      />
    </LntDropdown>
  )
}
